import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { searchPatients } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { DoctorAPI, DoctorListItem } from "../../services/doctor.service";

export type SearchMode = "patients" | "doctors" | "both";

const PLACEHOLDER_BY_MODE: Record<SearchMode, string> = {
  patients: "Search patients by name or phone...",
  doctors: "Search doctors by name or specialization...",
  both: "Search patients or doctors...",
};

// ── Keyword → page shortcuts ─────────────────────────────────────────────────

interface Shortcut {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const SHORTCUTS: Array<{ keywords: string[]; shortcut: Shortcut }> = [
  {
    keywords: ["appointment", "appointments"],
    shortcut: {
      label: "Appointments",
      path: "/appointments",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" />
        </svg>
      ),
    },
  },
  {
    keywords: ["patient", "patients"],
    shortcut: {
      label: "Patients",
      path: "/patients",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
  },
  {
    keywords: ["doctor", "doctors"],
    shortcut: {
      label: "Doctors",
      path: "/doctors",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" /><path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" /><circle cx="20" cy="10" r="2" />
        </svg>
      ),
    },
  },
  {
    keywords: ["pharmacy", "medicine", "medicines", "drugs"],
    shortcut: {
      label: "Pharmacy",
      path: "/pharmacy",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path d="m8.5 8.5 7 7" />
        </svg>
      ),
    },
  },
  {
    keywords: ["report", "reports", "analytics", "revenue", "analytics"],
    shortcut: {
      label: "Reports & Analytics",
      path: "/reports",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" />
        </svg>
      ),
    },
  },
  {
    keywords: ["billing", "bill", "invoice", "payment"],
    shortcut: {
      label: "Billing",
      path: "/billing",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
    },
  },
  {
    keywords: ["setting", "settings", "profile", "clinic"],
    shortcut: {
      label: "Settings",
      path: "/settings",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" />
        </svg>
      ),
    },
  },
];

function matchShortcut(query: string): Shortcut | null {
  const q = query.trim().toLowerCase();
  if (!q) return null;
  for (const { keywords, shortcut } of SHORTCUTS) {
    if (keywords.some((k) => k.startsWith(q) || q.startsWith(k))) {
      return shortcut;
    }
  }
  return null;
}

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  className?: string;
  /** Which entity type(s) to search — defaults to patients-only, matching
   * every page except Doctors (doctors-only) and Appointments (both). */
  mode?: SearchMode;
}

export const GlobalSearchBar: React.FC<Props> = ({ className = "", mode = "patients" }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<PatientDbRecord[]>([]);
  const [doctorResults, setDoctorResults] = useState<DoctorListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const searchPatientsEnabled = mode === "patients" || mode === "both";
  const searchDoctorsEnabled = mode === "doctors" || mode === "both";

  const shortcut = matchShortcut(query);
  const hasDropdown = open && query.trim().length > 0 && (shortcut !== null || results.length > 0 || doctorResults.length > 0 || searching || query.trim().length >= 2);

  // Debounced patient/doctor search, depending on mode
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setDoctorResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const [patientHits, doctorHits] = await Promise.all([
          searchPatientsEnabled ? searchPatients(q, 8).catch(() => []) : Promise.resolve([]),
          searchDoctorsEnabled ? DoctorAPI.list({ search: q }).catch(() => []) : Promise.resolve([]),
        ]);
        setResults(patientHits);
        setDoctorResults(doctorHits.slice(0, 8));
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchPatientsEnabled, searchDoctorsEnabled]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      const q = query.trim();
      if (!q) return;
      if (shortcut) {
        navigate(shortcut.path);
      } else if (results.length === 1 && doctorResults.length === 0) {
        const p = results[0];
        if (p.display_id) {
          navigate(`/patients/${p.display_id}`, { state: { patient: p } });
        } else {
          navigate(`/patients?search=${encodeURIComponent(displayName(p))}`);
        }
      } else if (doctorResults.length === 1 && results.length === 0) {
        navigate(`/doctors?search=${encodeURIComponent(doctorResults[0].name)}`);
      } else if (searchDoctorsEnabled && !searchPatientsEnabled) {
        navigate(`/doctors?search=${encodeURIComponent(q)}`);
      } else {
        navigate(`/patients?search=${encodeURIComponent(q)}`);
      }
      setOpen(false);
      setQuery("");
    }
  };

  const goToPatient = (p: PatientDbRecord) => {
    if (p.display_id) {
      navigate(`/patients/${p.display_id}`, { state: { patient: p } });
    } else {
      navigate(`/patients?search=${encodeURIComponent(displayName(p))}`);
    }
    setOpen(false);
    setQuery("");
  };

  const goToDoctor = (d: DoctorListItem) => {
    navigate(`/doctors?search=${encodeURIComponent(d.name)}`);
    setOpen(false);
    setQuery("");
  };

  const goToShortcut = (path: string) => {
    navigate(path);
    setOpen(false);
    setQuery("");
  };

  const displayName = (p: PatientDbRecord) =>
    p.name || `${p.first_name ?? ""} ${p.last_name ?? ""}`.trim() || "Unknown";

  return (
    <div ref={containerRef} className={`relative min-w-0 flex-1 ${className}`}>
      {/* Input */}
      <div className="relative">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
          fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"
        >
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
        </svg>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => query.trim() && setOpen(true)}
          onKeyDown={handleKeyDown}
          className="w-64 max-w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white"
          placeholder={PLACEHOLDER_BY_MODE[mode]}
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {/* Dropdown */}
      {hasDropdown && (
        <div className="absolute top-full mt-1.5 left-0 w-72 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
          {/* Page shortcut */}
          {shortcut && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2.5 pb-1">Go to page</p>
              <button
                onMouseDown={(e) => { e.preventDefault(); goToShortcut(shortcut.path); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
              >
                <span className="text-blue-600">{shortcut.icon}</span>
                <span className="text-sm font-medium text-slate-800">{shortcut.label}</span>
                <svg className="w-3.5 h-3.5 text-slate-400 ml-auto" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Patient results */}
          {results.length > 0 && (
            <div className={shortcut ? "border-t border-slate-100" : ""}>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2.5 pb-1">Patients</p>
              {results.map((p) => (
                <button
                  key={p.patient_id}
                  onMouseDown={(e) => { e.preventDefault(); goToPatient(p); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
                >
                  <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center shrink-0">
                    {(p.first_name?.[0] ?? "?").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{displayName(p)}</p>
                    {p.display_id && (
                      <p className="text-xs text-slate-400">{p.display_id}</p>
                    )}
                  </div>
                  <svg className="w-3.5 h-3.5 text-slate-400 ml-auto shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {/* Doctor results */}
          {doctorResults.length > 0 && (
            <div className={shortcut || results.length > 0 ? "border-t border-slate-100" : ""}>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide px-3 pt-2.5 pb-1">Doctors</p>
              {doctorResults.map((d) => (
                <button
                  key={d.doctor_id}
                  onMouseDown={(e) => { e.preventDefault(); goToDoctor(d); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 text-left"
                >
                  <div className="w-7 h-7 rounded-full bg-fuchsia-50 text-fuchsia-600 text-xs font-semibold flex items-center justify-center shrink-0">
                    {(d.name?.[0] ?? "?").toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.name}</p>
                    <p className="text-xs text-slate-400 truncate">{d.specialization}</p>
                  </div>
                  <svg className="w-3.5 h-3.5 text-slate-400 ml-auto shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}

          {/* No results found (only after searching, not during shortcut-only state) */}
          {!searching && results.length === 0 && doctorResults.length === 0 && !shortcut && query.trim().length >= 2 && (
            <p className="text-xs text-slate-400 px-3 py-3">
              No {mode === "doctors" ? "doctors" : mode === "both" ? "patients or doctors" : "patients"} found for "{query.trim()}"
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearchBar;
